// Pure, side-effect-free helpers for launching the dev servers in a way that
// SURVIVES the parent process / Claude Code harness exiting.
//
// Why this exists: a child launched from inside the harness (PowerShell
// `run_in_background`, or a plain `spawn(..., { detached: true })`) inherits the
// harness Job Object and is reaped when the job closes (~1-3 min). A process
// created by the WMI service (`Win32_Process.Create`) is born OUTSIDE that job —
// the same reason a terminal opened by explorer.exe survives. These builders emit
// the PowerShell that performs that WMI launch; the actual spawn + I/O lives in
// src/commands/dev.ts. Tested by src/lib/__tests__/dev-launch.test.ts.

/** Escape a value for safe embedding inside a PowerShell single-quoted string. */
function psSingle(v: string): string {
  return v.replace(/'/g, "''");
}

export interface LaunchOpts {
  /** Absolute working directory the server runs in. */
  workdir: string;
  /** The command to run, e.g. `dotnet run --launch-profile Local`. */
  cmd: string;
  /** Absolute path for captured stdout (hidden mode only). */
  outLog: string;
  /** Absolute path for captured stderr (hidden mode only). */
  errLog: string;
  /** Show a console window with live output instead of redirecting to log files. */
  visible?: boolean;
}

/**
 * Build a one-line PowerShell `-Command` script that launches `cmd` via the WMI
 * service (`Win32_Process.Create`). The created process is NOT a member of the
 * caller's Job Object, so it outlives the harness turn/session — exactly what an
 * external terminal does. The script prints the created PID (the wrapping
 * `cmd.exe`) to stdout, or fails (exit 1) if WMI returns a non-zero ReturnValue.
 *
 * - hidden  (default): `ShowWindow = 0` (SW_HIDE); stdout/stderr go to log files.
 * - visible:           `ShowWindow = 1`; `cmd /k` keeps the window open with live output.
 *
 * `>` is a cmd.exe shell operator (not understood by CreateProcess), so the inner
 * command is always wrapped in `cmd /c|/k`. cmd strips the outer quote pair, leaving
 * the embedded quoted paths intact (handles spaces, e.g. `01 - projets`).
 */
export function buildDetachedLaunchPwsh(opts: LaunchOpts): string {
  const wd = psSingle(opts.workdir);
  const cmd = psSingle(opts.cmd);
  const out = psSingle(opts.outLog);
  const err = psSingle(opts.errLog);

  const inner = opts.visible
    ? `cmd /k "${cmd}"`
    : `cmd /c "${cmd} 1> "${out}" 2> "${err}""`;

  const show = opts.visible ? 1 : 0;

  return [
    `$ErrorActionPreference='Stop'`,
    `$si = New-CimInstance -ClassName Win32_ProcessStartup -ClientOnly -Property @{ ShowWindow = [uint16]${show} }`,
    `$r = Invoke-CimMethod -ClassName Win32_Process -MethodName Create -Arguments @{ CommandLine = '${inner}'; CurrentDirectory = '${wd}'; ProcessStartupInformation = $si }`,
    `if ($r.ReturnValue -ne 0) { Write-Error ('Win32_Process.Create failed: ' + $r.ReturnValue); exit 1 }`,
    `$r.ProcessId`,
  ].join('; ');
}
